import { get } from '@shared/adapters/lodash'
import { formatDisplayDate, formatUnsignedMoney } from '@shared/utils'
import { formatApiPercent } from '../../utils/portfolioCompositionUtils'
import type { Investment } from '../../types/investmentTypes'
import type {
  PortfolioAllocation,
  PortfolioAllocationSlice,
  PortfolioAppreciation,
  PortfolioChangePeriod,
  PortfolioGraphRental,
  PortfolioGraphValue,
  PortfolioHoldingsSummary,
  PortfolioMoney,
  PortfolioOverview,
  RentalIncomeSummary,
} from '../../types/portfolioTypes'

const DEFAULT_CURRENCY = 'AED'
const PERCENT_BASE = 100
const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 10

// Per-shape helpers keep each parser function small and reduce the
// repeated `get(item, 'a.b') ?? default` complexity ESLint flagged.

const parseMoney = (resp: unknown, key: string) => ({
  value: get(resp, `${key}.value`) ?? 0,
  currency: get(resp, `${key}.currency`) ?? DEFAULT_CURRENCY,
})

// Graph months that haven't happened yet arrive as null — unknown, not zero —
// so the chart can end the line at the last real point. Anything non-numeric
// is treated the same way.
const parseSeriesValue = (point: unknown, key: string, snakeKey: string): number | null => {
  const raw: unknown = get(point, key) ?? get(point, snakeKey)
  if (raw === null || raw === undefined || raw === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

const parseMoneyWithChange = (resp: unknown, key: string) => ({
  ...parseMoney(resp, key),
  changePercentage:
    get(resp, `${key}.changePercentage`) ?? get(resp, `${key}.change_percentage`) ?? 0,
})

interface ParsedHolding {
  propertyId: string
  offeringId: string
  propertyName: string
  propertyLocation: string
  coverImage: string
  tokensHeld: number
  investedAmount: number
  currentInvestmentValue: number
  rentalIncomeEarned: number
  currency: string
  totalReturns: { amount: number; currency: string; percentage: number }
}

const parseHolding = (item: unknown): ParsedHolding => {
  const currentInvestmentValue = Number(get(item, 'currentInvestmentValue') ?? 0)
  const returnsAmount = Number(get(item, 'totalReturns.amount') ?? 0)
  const rentalIncomeEarned = Number(get(item, 'rentalIncomeEarned') ?? 0)
  // Older payloads omit `investedAmount`; back it out of current value minus
  // the appreciation half of the returns (returns = appreciation + rent).
  const fallbackInvested = currentInvestmentValue - (returnsAmount - rentalIncomeEarned)

  return {
    propertyId: get(item, 'propertyId') ?? '',
    offeringId: get(item, 'offeringId') ?? '',
    propertyName: get(item, 'propertyName') ?? '',
    propertyLocation: get(item, 'propertyLocation') ?? '',
    coverImage: get(item, 'coverImage') ?? '',
    tokensHeld: Number(get(item, 'tokensHeld') ?? 0),
    investedAmount: Number(get(item, 'investedAmount') ?? Math.max(fallbackInvested, 0)),
    currentInvestmentValue,
    rentalIncomeEarned,
    currency: get(item, 'currency') ?? DEFAULT_CURRENCY,
    totalReturns: {
      amount: returnsAmount,
      currency: get(item, 'totalReturns.currency') ?? get(item, 'currency') ?? DEFAULT_CURRENCY,
      percentage: Number(get(item, 'totalReturns.percentage') ?? 0),
    },
  }
}

const holdingToInvestment = (holding: ParsedHolding): Investment => {
  const appreciation = holding.currentInvestmentValue - holding.investedAmount

  return {
    id: holding.propertyId,
    offeringId: holding.offeringId,
    imageUrl: holding.coverImage,
    title: holding.propertyName,
    location: holding.propertyLocation,
    tokens: holding.tokensHeld,
    invested: formatUnsignedMoney(holding.currency, holding.investedAmount),
    currentValue: formatUnsignedMoney(holding.currency, holding.currentInvestmentValue),
    appreciation: formatUnsignedMoney(holding.currency, Math.abs(appreciation)),
    appreciationPositive: appreciation >= 0,
    rentEarned: formatUnsignedMoney(holding.currency, holding.rentalIncomeEarned),
    totalReturn: formatUnsignedMoney(holding.totalReturns.currency, Math.abs(holding.totalReturns.amount)),
    // Exactly as the API sends it (2.07 → "2.07%"); only >3 decimals are rounded.
    totalReturnPercent: formatApiPercent(holding.totalReturns.percentage),
    totalReturnPositive: holding.totalReturns.amount >= 0,
  }
}

const CHANGE_PERIODS: readonly PortfolioChangePeriod[] = ['since_purchase', '30d', 'mtd']
const DEFAULT_CHANGE_PERIOD: PortfolioChangePeriod = 'since_purchase'

const parseChangePeriod = (raw: unknown): PortfolioChangePeriod =>
  CHANGE_PERIODS.find((period) => period === raw) ?? DEFAULT_CHANGE_PERIOD

// The backend owns appreciation (capital gain, excluding rent). Only an older
// payload without the key falls back to value − invested, so screens never blank.
const parseAppreciation = (
  data: unknown,
  invested: PortfolioMoney,
  current: PortfolioMoney,
): PortfolioAppreciation => {
  const value: unknown = get(data, 'totalAppreciation.value')
  if (typeof value === 'number') {
    return {
      value,
      currency: get(data, 'totalAppreciation.currency') ?? current.currency,
      percentage: Number(get(data, 'totalAppreciation.percentage') ?? 0),
    }
  }
  const gain = current.value - invested.value
  return {
    value: gain,
    currency: current.currency,
    percentage: invested.value > 0 ? (gain / invested.value) * PERCENT_BASE : 0,
  }
}

export const parsePortfolioOverview = (resp: unknown): PortfolioOverview => {
  const data = get(resp, 'data') ?? resp
  const totalInvested = parseMoney(data, 'totalInvested')
  const currentPortfolioValue = {
    ...parseMoneyWithChange(data, 'currentPortfolioValue'),
    changePeriod: parseChangePeriod(get(data, 'currentPortfolioValue.changePeriod')),
  }
  return {
    totalInvested,
    currentPortfolioValue,
    totalAppreciation: parseAppreciation(data, totalInvested, currentPortfolioValue),
    totalRentalIncome: parseMoneyWithChange(data, 'totalRentalIncome'),
    projectedRoi: {
      percentage: get(data, 'projectedRoi.percentage') ?? 0,
    },
    lastUpdatedAt: get(data, 'lastUpdatedAt') ?? '',
  }
}

/** `availableFiscalYears` as sent (newest first); only non-empty strings survive. */
const parseFiscalYears = (data: unknown): string[] => {
  const raw: unknown = get(data, 'availableFiscalYears')
  if (!Array.isArray(raw)) return []
  return raw.filter((year): year is string => typeof year === 'string' && year !== '')
}

export const parsePortfolioGraphValue = (resp: unknown): PortfolioGraphValue => {
  const data = get(resp, 'data') ?? resp
  return {
    type: 'value',
    currency: get(data, 'currency') ?? DEFAULT_CURRENCY,
    startDate: get(data, 'startDate') ?? get(data, 'start_date') ?? '',
    availableFiscalYears: parseFiscalYears(data),
    series: ((get(data, 'series') ?? []) as unknown[]).map((point) => ({
      month: get(point, 'month') ?? '',
      investedAmount: parseSeriesValue(point, 'investedAmount', 'invested_amount'),
      portfolioValue: parseSeriesValue(point, 'portfolioValue', 'portfolio_value'),
    })),
  }
}

export const parsePortfolioGraphRental = (resp: unknown): PortfolioGraphRental => {
  const data = get(resp, 'data') ?? resp
  return {
    type: 'rental',
    currency: get(data, 'currency') ?? DEFAULT_CURRENCY,
    startDate: get(data, 'startDate') ?? get(data, 'start_date') ?? '',
    availableFiscalYears: parseFiscalYears(data),
    series: ((get(data, 'series') ?? []) as unknown[]).map((point) => ({
      month: get(point, 'month') ?? '',
      rentalIncome: parseSeriesValue(point, 'rentalIncome', 'rental_income'),
    })),
  }
}

export const parsePortfolioHoldingsSummary = (resp: unknown): PortfolioHoldingsSummary => {
  const data = get(resp, 'data') ?? resp
  return {
    totalTokensHeld: get(data, 'totalTokensHeld') ?? get(data, 'total_tokens_held') ?? 0,
    propertiesInvested: get(data, 'propertiesInvested') ?? get(data, 'properties_invested') ?? 0,
    totalPortfolioValue:
      get(data, 'totalPortfolioValue') ?? get(data, 'total_portfolio_value') ?? 0,
    currency: get(data, 'currency') ?? DEFAULT_CURRENCY,
  }
}

export const parseRentalIncomeSummary = (resp: unknown): RentalIncomeSummary => {
  const data = get(resp, 'data') ?? resp
  const last = get(data, 'lastReceived') ?? {}
  const next = get(data, 'nextMonth') ?? {}

  const lastCurrency = String(get(last, 'currencyCode') ?? DEFAULT_CURRENCY)
  const nextCurrency = String(get(next, 'currencyCode') ?? DEFAULT_CURRENCY)
  const lastReceivedAt: string | null = get(last, 'receivedAt') ?? null
  const nextExpectedAt: string = String(get(next, 'expectedAt') ?? '')

  return {
    lastReceived: {
      amount: formatUnsignedMoney(lastCurrency, Number(get(last, 'amount') ?? 0)),
      receivedAt: lastReceivedAt ? formatDisplayDate(new Date(lastReceivedAt)) : null,
    },
    nextMonth: {
      amount: formatUnsignedMoney(nextCurrency, Number(get(next, 'amount') ?? 0)),
      expectedAt: nextExpectedAt ? formatDisplayDate(new Date(nextExpectedAt)) : '',
      expectedAtIso: nextExpectedAt,
      breakdown: ((get(next, 'breakdown') ?? []) as unknown[]).map((item) => ({
        propertyId: String(get(item, 'propertyId') ?? ''),
        propertyName: String(get(item, 'propertyName') ?? ''),
        propertyLocation: String(get(item, 'propertyLocation') ?? ''),
        coverImage: String(get(item, 'coverImage') ?? ''),
        amount: formatUnsignedMoney(
          String(get(item, 'currencyCode') ?? nextCurrency),
          Number(get(item, 'amount') ?? 0),
        ),
      })),
    },
  }
}

export const parsePortfolioHoldings = (
  resp: unknown,
): { rows: Investment[]; page: number; limit: number; rowsCount: number } => {
  const rows = (get(resp, 'data.rows') ?? []) as unknown[]
  return {
    rows: rows.map(parseHolding).map(holdingToInvestment),
    page: Number(get(resp, 'data.page') ?? DEFAULT_PAGE),
    limit: Number(get(resp, 'data.limit') ?? DEFAULT_PAGE_SIZE),
    rowsCount: Number(get(resp, 'data.rowsCount') ?? rows.length ?? 0),
  }
}

/**
 * `/portfolio/allocation` → donut slices. The backend sends every holding,
 * largest first, with shares that add up to 100 — rendered as sent. Holdings
 * with nothing invested draw no slice.
 */
export const parsePortfolioAllocation = (resp: unknown): PortfolioAllocation => {
  const data = get(resp, 'data') ?? resp
  const currency = String(get(data, 'currency') ?? DEFAULT_CURRENCY)
  const items = (get(data, 'items') ?? []) as unknown[]
  const slices: PortfolioAllocationSlice[] = items
    .map((item) => ({
      id: String(get(item, 'propertyId') ?? ''),
      name: String(get(item, 'propertyName') ?? ''),
      location: String(get(item, 'propertyLocation') ?? ''),
      imageUrl: '',
      investedAmount: Number(get(item, 'investedAmount') ?? 0),
      investedLabel: '',
      sharePercent: Math.round(Number(get(item, 'sharePercentage') ?? 0)),
    }))
    .filter((slice) => slice.investedAmount > 0)
    .map((slice) => ({ ...slice, investedLabel: formatUnsignedMoney(currency, slice.investedAmount) }))
  return {
    slices,
    propertyCount: Number(get(data, 'propertiesCount') ?? slices.length),
    currency,
  }
}
